/**
 * Single session recording card.
 */
import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { Box } from "@mui/material";
import { useParams } from "react-router-dom";
import Vimeo from "@u-wave/react-vimeo";
import { useTranslation } from "react-i18next";
import { useCallback } from "react";
import PropTypes from "prop-types";

import { BModal, BThumb } from "components";
import { VText, VButton, VAvatar } from "form";
import { actionReplayInspectEdit } from "context/action";
import { useAsterController } from "context";
import SafeHtml from "pages/common/SafeHtml";
import { asFunc, asObject } from "utils/safeData";

/**
 * Resolve a recording `file_id` to an S3/HTTP player or a legacy Vimeo id.
 *
 * @param {*} fileId Stored recording id (URL, S3 key, or Vimeo id).
 * @returns {{ kind: "url"|"vimeo"|"none", src: string, poster: string }}
 */
const replayMedia = (fileId) => {
  const id = String(fileId || "").trim();
  if (!id) return { kind: "none", src: "", poster: "" };
  if (/^https?:\/\//i.test(id) || /\.(mp4|webm|mkv|mov)(\?|$)/i.test(id)) {
    return { kind: "url", src: id, poster: id.replace(/\.(mp4|webm|mkv|mov)(\?|$)/i, ".jpg$2") };
  }
  if (id.includes("/") && !/^\d+$/.test(id)) {
    const base = String(process.env.REACT_APP_S3_ENDPOINT || "").replace(/\/$/, "");
    const src = `${base}/${id.replace(/^\/+/, "")}`;
    return { kind: "url", src, poster: src.replace(/\.(mp4|webm|mkv|mov)(\?|$)/i, ".jpg$2") };
  }
  return { kind: "vimeo", src: id, poster: "" };
};

/**
 * Card thumbnail: a still frame from the recording, with a video fallback.
 *
 * @param {{ media: { src: string, poster: string } }} props
 */
const ReplayCardThumb = ({ media }) => {
  const [useVideo, setUseVideo] = useState(!media.poster);

  return (
    <Box className="relative w-full aspect-[5/3] rounded-md overflow-hidden bg-black">
      {useVideo ? (
        <video
          className="absolute inset-0 w-full h-full object-cover"
          src={media.src}
          poster={media.poster}
          muted
          playsInline
          preload="metadata"
          onLoadedMetadata={(event) => {
            const video = event.currentTarget;
            if (Number.isFinite(video.duration) && video.duration > 1) {
              video.currentTime = Math.min(3, video.duration * 0.25);
            }
          }}
        />
      ) : (
        <img
          src={media.poster}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          onError={() => setUseVideo(true)}
        />
      )}
      <Box className="absolute inset-0 flex items-center justify-center pointer-events-none bg-black/20">
        <Icon icon="mdi:play-circle" className="text-white text-5xl opacity-95 drop-shadow" />
      </Box>
    </Box>
  );
};

/**
 * Recording row that plays an S3 (or legacy Vimeo) recording and tracks watch time.
 *
 * @param {{ session: Object, setParentTab?: Function }} props
 */
const CourseReplayItem = (props) => {
  const { t } = useTranslation("common");

  const session = asObject(props.session);
  const setParentTab = asFunc(props.setParentTab);
  const { courseId } = useParams();

  const [controller, dispatch] = useAsterController();
  const { user } = controller;

  const [open, setOpen] = useState(false);
  const media = replayMedia(session.file_id);

  /**
   * Persists one minute of replay watch time.
   *
   * @returns {void}
   */
  const onSave = useCallback(() => {
    // Save
    const data = {
      course_id: courseId,
      event_id: session.room,
      file_id: session.file_id,
      topic: session.topic,
      spent: 60,
      instructor_name: session.tutor_name,
      hash: user.hash,
    };
    if (window.replayInterval) actionReplayInspectEdit(dispatch, data);
  }, [courseId, session, user, dispatch]);

  /**
   * Starts a one-minute interval that records replay watch time.
   *
   * @returns {void}
   */
  const startTimetrack = useCallback(() => {
    window.replayInterval = setInterval(onSave, 1000 * 60 * 1);
  }, [onSave]);
  /**
   * Stops the replay watch-time interval.
   *
   * @returns {void}
   */
  const stopTimetrack = () => {
    if (window.replayInterval) clearInterval(window.replayInterval);
    window.replayInterval = null;
  };

  useEffect(() => {
    window.addEventListener("blur", stopTimetrack);
  }, []);

  useEffect(() => {
    window.addEventListener("focus", () => {
      if (open) startTimetrack();
    });
    if (open) {
      startTimetrack();
    } else {
      stopTimetrack();
    }
    return () => stopTimetrack();
  }, [open, startTimetrack]);

  /**
   * Opens the replay player and switches the parent tab.
   *
   * @returns {void}
   */
  const onPlayClick = () => {
    setOpen(true);
    if (setParentTab) setParentTab(4);
  };

  return (
    <>
      <BModal open={open} setOpen={setOpen} width="90vw" height="fit-content" className="w-full max-w-xl max-h-150 p-8">
        <VText className="font-bold flex text-xl mr-8" div>
          {session.topic}
        </VText>
        <Box className="w-full">
          <VText>{session.tutor_name}</VText>
        </Box>
        <VText color="secondary" className="mt-4" div>
          <SafeHtml html={session.description} />
        </VText>

        <Box className="h-80 my-4 overflow-hidden">
          {media.kind === "url" ? (
            <video
              className="w-full h-full shadow-lg bg-black"
              src={media.src}
              poster={media.poster}
              controls
              playsInline
              preload="metadata"
              controlsList="nodownload"
            >
              Your browser does not support the video tag.
            </video>
          ) : media.kind === "vimeo" ? (
            <Vimeo video={media.src} className="w-full shadow-lg" autopause controls responsive color="#f76060" />
          ) : null}
        </Box>
      </BModal>

      <Box className="w-full border border-solid border-gray-300 dark:border-gray-500 rounded-md h-full flex flex-col">
        <Box className="flex flex-col w-full relative">
          {media.kind === "url" ? (
            <ReplayCardThumb media={media} />
          ) : (
            <BThumb src={`${process.env.REACT_APP_S3_ASSET_ENDPOINT}/v2-assets/app/video1.png`} error="" className="w-full aspect-[5/3] rounded-md" />
          )}
          <Box className="flex items-end -mt-11 lg:-mt-14 ml-4">
            <VAvatar src={session.tutor_avatar} className="w-20 h-20 lg:w-24 lg:h-24" />
            <VText className="text-lg">{session.tutor_name}</VText>
          </Box>
        </Box>
        <Box className="p-4 flex flex-col gap-1">
          <VText div className="text-limit-2">
            {session?.topic || "-"}
          </VText>
          <VText color="secondary" className="flex items-center text-xs">
            <Icon icon="lucide:clock-9" />
            &nbsp;
            {session.start_time
              ? (() => {
                  const dt = new Date(session.start_time);
                  return Number.isNaN(dt.getTime()) ? session.start_time : dt.toLocaleString();
                })()
              : "-"}
          </VText>
        </Box>
        <Box className="p-4 mt-auto" onClick={() => onPlayClick(true)}>
          <VButton variant="contained" color="primary" className="w-full normal-case">
            {t("Replay")}
          </VButton>
        </Box>
      </Box>
    </>
  );
};

CourseReplayItem.propTypes = {
  session: PropTypes.object,
  setParentTab: PropTypes.func,
};

export default CourseReplayItem;
